/* Native Shopify contact remains responsible for POST, captcha and delivery.
   Only a form ID, the Page pathname and a five-minute expiry are stored. */
(() => {
  'use strict';
  const boot = 'data-yl-native-contact-feedback-loaded';
  if (document.documentElement.hasAttribute(boot)) return;
  document.documentElement.setAttribute(boot, '');
  const storageKey = 'yl-native-contact-pending-v1', expiryMs = 5 * 60 * 1000;
  const ownerSelector = '[data-yl-native-contact-owner]';
  const feedbackSelector = '[data-yl-native-contact-feedback]';
  const focused = new WeakSet();

  function contactAction(form, id, prefix) {
    try {
      const raw = form.getAttribute('action');
      if (!raw || form.getAttribute('method')?.toLowerCase() !== 'post' || (form.getAttribute('target') && form.getAttribute('target') !== '_self')) return false;
      const url = new URL(raw, location.href);
      return url.origin === location.origin && !url.username && !url.password && !url.search && url.pathname === `${prefix}/contact` && url.hash === `#${id}`;
    } catch { return false; }
  }

  function collect() {
    const owners = [...document.querySelectorAll(ownerSelector)];
    const forms = [...document.querySelectorAll('form.yl-native-contact-form')];
    const ids = new Map();
    for (const node of document.querySelectorAll('[id]')) ids.set(node.id, (ids.get(node.id) || 0) + 1);
    const records = [], states = new Set();
    let pageId, handle;
    for (const owner of owners) {
      const ownPage = owner.dataset.pageId, ownHandle = owner.dataset.pageHandle, id = owner.dataset.formId;
      if (!/^[1-9]\d{0,19}$/.test(ownPage || '') || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(ownHandle || '')) return null;
      if (pageId && (pageId !== ownPage || handle !== ownHandle)) return null;
      pageId = ownPage; handle = ownHandle;
      const match = new RegExp(`^yl-native-contact-${ownPage}-(m-[1-9]\\d{0,19})$`).exec(id || '');
      const path = new RegExp(`^(/[a-z]{2}(?:-[a-z]{2})?)?/pages/${ownHandle}$`).exec(location.pathname);
      if (!match || !path || owner.parentElement?.closest(ownerSelector)) return null;
      const prefix = path[1] || '';
      const children = [...owner.children];
      if (children.length !== 1 || children[0].tagName !== 'FORM') return null;
      const form = children[0];
      if (!forms.includes(form) || form.id !== id || ids.get(id) !== 1 || !contactAction(form, id, prefix)) return null;
      const replies = [...form.querySelectorAll(feedbackSelector)];
      if (replies.length > 1) return null;
      const feedback = replies[0], state = feedback?.dataset.ylNativeContactFeedback;
      if (feedback) {
        const suffix = state === 'success' ? 'success' : state === 'error' ? 'errors' : null;
        if (!suffix || feedback.parentElement !== form || feedback.id !== `${id}-${suffix}` || ids.get(feedback.id) !== 1 || feedback.getAttribute('role') !== (state === 'error' ? 'alert' : 'status') || feedback.getAttribute('tabindex') !== '-1') return null;
        states.add(state);
      }
      records.push({ owner, form, id, prefix, feedback, state });
    }
    if (!records.length || records.length > 2 || records.length !== forms.length || new Set(records.map(record => record.id)).size !== records.length || states.size > 1) return null;
    return records;
  }

  function pending(records) {
    let raw;
    try { raw = sessionStorage.getItem(storageKey); }
    catch { return { kind: 'unavailable' }; }
    if (raw === null) return { kind: 'missing' };
    try {
      const value = JSON.parse(raw), now = Date.now();
      if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).sort().join(',') !== 'expiresAt,formId,path' || !Number.isSafeInteger(value.expiresAt) || value.expiresAt <= now || value.expiresAt > now + expiryMs || value.path !== location.pathname || !records.some(record => record.id === value.formId)) return { kind: 'invalid' };
      return { kind: 'valid', id: value.formId };
    } catch { return { kind: 'invalid' }; }
  }

  function clearErrorAria(form) {
    const errorId = `${form.id}-errors`;
    for (const field of form.querySelectorAll('[aria-describedby]')) {
      const tokens = field.getAttribute('aria-describedby').split(/\s+/).filter(Boolean);
      if (!tokens.includes(errorId)) continue;
      const keep = tokens.filter(token => token !== errorId);
      if (keep.length) field.setAttribute('aria-describedby', keep.join(' '));
      else field.removeAttribute('aria-describedby');
      if (field.getAttribute('aria-invalid') === 'true') field.removeAttribute('aria-invalid');
    }
  }

  function hideFeedback() {
    for (const owner of document.querySelectorAll(ownerSelector)) {
      owner.removeAttribute('data-yl-native-contact-ready');
      owner.setAttribute('data-yl-native-contact-refused', '');
      for (const feedback of owner.querySelectorAll(feedbackSelector)) {
        feedback.hidden = true; feedback.removeAttribute('data-yl-native-contact-active');
      }
    }
  }

  // Observe at window bubble, after form/document validation and captcha handlers.
  // Never prevent, redirect, serialize or change fields. If a provider stops
  // propagation or submits without an event, the native fragment is the fallback.
  window.addEventListener('submit', event => {
    if (event.defaultPrevented) return;
    const current = collect()?.find(item => item.form === event.target);
    const submitter = event.submitter;
    if (!current || (submitter && (submitter.form !== current.form || ['formaction', 'formmethod', 'formtarget'].some(name => submitter.hasAttribute(name))))) return;
    try { sessionStorage.setItem(storageKey, JSON.stringify({ formId: current.id, path: location.pathname, expiresAt: Date.now() + expiryMs })); }
    catch { /* The unchanged native action fragment is the fallback. */ }
  });

  function initialize() {
    hideFeedback();
    const records = collect();
    if (!records) {
      for (const form of document.querySelectorAll('form.yl-native-contact-form')) clearErrorAria(form);
      return;
    }
    for (const record of records) {
      record.owner.removeAttribute('data-yl-native-contact-refused');
      record.owner.setAttribute('data-yl-native-contact-ready', '');
    }
    const stored = pending(records), hash = location.hash;
    const hashId = records.find(record => hash === `#${record.id}`)?.id;
    // A stale/malformed/cross-Page record or a conflicting fragment cannot be
    // bypassed with a hash. With no saved record (or denied storage), the exact
    // native form fragment can identify one reply; the fragment has no age proof.
    let id = stored.kind === 'valid' ? ((!hash || hashId === stored.id) ? stored.id : null)
      : (stored.kind === 'missing' || stored.kind === 'unavailable') ? hashId : null;
    const previous = records.filter(record => record.feedback && focused.has(record.feedback));
    if (!id && stored.kind === 'missing' && !hash && previous.length === 1) id = previous[0].id;
    const active = records.find(record => record.id === id && record.feedback);
    for (const record of records) if (record !== active || record.state !== 'error') clearErrorAria(record.form);
    if (!active) return;
    active.feedback.hidden = false;
    active.feedback.setAttribute('data-yl-native-contact-active', '');
    for (let parent = active.form.parentElement; parent; parent = parent.parentElement) if (parent.tagName === 'DETAILS') parent.open = true;
    if (!focused.has(active.feedback)) { focused.add(active.feedback); active.feedback.focus(); }
    if (stored.kind === 'valid') {
      try { sessionStorage.removeItem(storageKey); } catch { /* No values were stored. */ }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
